import { startTransition, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { IActionFormProps, TableAdapter } from '@makyoapp/makyo-common'
import { Search16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, Field, Label, SimpleInput } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { ActionInputdata } from '@/shared/action.types'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { useDebounceInput } from '@/shared/makyo-hooks'
import { isEmpty } from '@/shared/utils'

import { ActionColumnField } from '../../shared/ActionColumnField'
import { getDefaultColumnValue, TableSelect } from '../../shared/TableSelect'

const filteredColumnTypes = [
  ColumnTypes.Slug,
  ColumnTypes.Template,
  ColumnTypes.Math,
  ColumnTypes.SingleCell,
  ColumnTypes.Function,
  ColumnTypes.Join,
  ColumnTypes.AISEO,
  ColumnTypes.Lookup,
  ColumnTypes.AITextProcessing,
  ColumnTypes.Connection,
  ColumnTypes.QRCode,
  ColumnTypes.Coordinate,
  ColumnTypes.Formula,
]

type UpdateRowFormValues = {
  // rowId: string
  tableId: string
  data: {
    [key: string]: ActionInputdata
  }
}

export default function ActFormUpdateRow({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  const { t } = useTranslation()
  const { tables } = useDbClientContext()
  const tblOptions = tables.filter((tb) => !tb.isSnapshot)

  const { activeComponent, activeLayerContainer } = useActiveLayerContext()
  const isDetailpage = !!activeLayerContainer?.tableId

  // get the tableId (datasource) from 'list' component
  const isListOrComment = activeComponent?.groupId === 'list' || activeComponent?.compId === 'comment'
  const elSettings = !activeComponent ? undefined : activeLayerContainer.elementSettings?.[activeComponent.id]
  const elSettingsDataSourceKey = !elSettings
    ? undefined
    : Object.keys(elSettings).find((key) => key.includes('data-source'))
  const elSettingsTableId =
    elSettings && elSettingsDataSourceKey ? (elSettings[elSettingsDataSourceKey] as string) : undefined

  // Get default tableId & col
  const defaultTableId = isListOrComment
    ? elSettingsTableId
    : isDetailpage
      ? activeLayerContainer.tableId
      : tblOptions[0]?.id

  // access form store
  const { form, onSubmit: onFormSubmit } = useForm<UpdateRowFormValues>({
    defaultValues: {
      // rowId: currentRowId,
      tableId: initialData?.tableId || defaultTableId || undefined,
      data: {
        ...(initialData?.data || {}),
      },
    },
    validate(values, setError) {
      let tbId = values.tableId

      if (!tbId || !tbId?.trim().length) {
        setError('tableId', 'Please choose a table')
      }
    },
  })

  const [debouncedSearch, , , currentSearch, setCurrentSearch] = useDebounceInput('')

  // derived form state
  const currentTableId = form.getValues('tableId')
  const currentTable = tblOptions.find((tb) => tb.id === currentTableId) ?? tblOptions[0]
  const tableColumns = currentTable?.columns

  const columnFields = useMemo(() => {
    return tableColumns
      ? tableColumns
          ?.filter((col) => !filteredColumnTypes.includes(col.type))
          .filter((col) => col.name.toLowerCase().includes(debouncedSearch.toLowerCase()))
      : []
  }, [tableColumns, debouncedSearch])

  // change handler
  const handleChangeTable = (tb: TableAdapter) => {
    startTransition(() => {
      form.setValues((prev) => {
        return {
          ...prev,
          tableId: tb.id,
        }
      })
    })

    // reset `data` field when `tableId` changes
    if (tb.id !== currentTableId && !isEmpty(tb.columns)) {
      let colData = tb.columns?.reduce((prev, curr) => {
        let currCol = curr
        return {
          ...prev,
          [currCol.id]: {
            value: getDefaultColumnValue(currCol.type),
            tableId: undefined,
            dataType: undefined,
            rowIndex: undefined,
          },
        }
      }, {})

      form.setValues('data', colData)
    }
  }

  // save action settings
  const handleSubmit = onFormSubmit((data) => {
    onSubmit(data)
  })

  return (
    <div>
      <div className='p-4 max-h-[75vh] overflow-auto'>
        <Field className='min-h-0'>
          <Label required>Table</Label>
          <TableSelect
            options={tblOptions}
            value={tblOptions.find((tb) => tb.id === form.getValues('tableId'))}
            onChange={(tb) => {
              handleChangeTable(tb)
            }}
            error={form.errors?.tableId}
          />
        </Field>

        <Field className='min-h-8!'>
          <Label>Target</Label>
          <div className='mt-2 text-light'>Current Row</div>
        </Field>

        {tableColumns && tableColumns.length > SHOW_SEARCH_LIMIT && (
          <>
            <hr className='my-4 bdr' />

            <Field className='min-h-0'>
              <Label></Label>
              <div className='flex-1 text-light'>
                <SimpleInput
                  value={currentSearch}
                  onChange={(e) => setCurrentSearch(e.target.value)}
                  placeholder={`${t('Search Column')} (${tableColumns?.length})`}
                  before={<Search16 className='mk-search-box-icon' />}
                />
              </div>
            </Field>
          </>
        )}

        {/* dynamic column field form based on selected table */}
        {!!columnFields?.length && (
          <div className='min-h-[250px]'>
            {columnFields?.map((col) => {
              const fieldValue = form.getValues('data')?.[col.id] ?? {}
              const fieldError = form.errors?.data?.[col.id]?.value

              return (
                <ActionColumnField
                  options={initialData?.actionOptions}
                  isActionWorkflow={initialData?.isActionWorkflow}
                  key={col.id}
                  value={fieldValue}
                  label={col.name}
                  type={col.type}
                  onChange={(val: any) => {
                    form.setValues('data', (prev) => ({
                      ...prev,
                      [col.id]: val,
                    }))
                  }}
                  error={fieldError}
                  colSettings={col.settings}
                />
              )
            })}
          </div>
        )}
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
